/**
 * Module 'release': the v1 signed release manifest for the node client (ADR 0042 decision 6,
 * design D14; R-SC-7, R-ND-6, R-ND-7, R-ON-3).
 *
 * One manifest names one version of the Go agent and, for each of the six targets, the file it
 * is published under, its size and its sha256, and optionally the same for each install package.
 * It also carries the floor (nodes below it update first), the revoked versions, an expiry, and the id of the release key that signed it. The
 * owner signs it offline (`orgus release sign`, docs/runbooks/release-signing.md) with one of two
 * release keys whose public halves are built into the node binary. The hub and the control plane
 * only mirror it: they never sign, and they never serve install scripts.
 *
 * The signed document, as published next to the binaries:
 *
 *   {"manifest": ReleaseManifest, "signature": base64(Ed25519(releaseSigningInput(manifest)))}
 *
 * The bytes that get signed are `orgus-release-manifest-v1\n` followed by the manifest as JSON
 * with keys sorted at every level, no whitespace, arrays in their given order. Every string in a
 * valid manifest is printable ASCII without quotes or backslashes, and every number is a safe
 * integer, so that serialisation is the same in every language (the Go verifier is
 * agents/node-agent/internal/release). Unknown fields are refused, never ignored, so nothing in a
 * document goes unsigned.
 *
 * Verification order, and the one reason each failure gives (both languages, test vectors in
 * __fixtures__/release-v1.json): not a valid document 'malformed'; key id not one of the trusted
 * keys 'unknown-key'; signature does not verify 'bad-signature'; now at or after `expires`
 * 'expired'.
 *
 * Browser-safe and dependency-free: the Ed25519 check is passed in (node:crypto in the hub and
 * the CLI, WebCrypto in a browser, crypto/ed25519 in Go).
 */

export const RELEASE_EXT = 'release';

/** The format tag inside every v1 manifest, and the first line of the bytes that get signed. */
export const RELEASE_MANIFEST_FORMAT = 'orgus-release-manifest-v1';

/** The six targets a release is built for, `<GOOS>-<GOARCH>` (R-ND-7). */
export const RELEASE_TARGETS = ['darwin-amd64', 'darwin-arm64', 'linux-amd64', 'linux-arm64', 'windows-amd64', 'windows-arm64'] as const;
export type ReleaseTarget = (typeof RELEASE_TARGETS)[number];

/**
 * The install packages a release may also carry, `<target>-<kind>` (R-ND-1, R-ND-7): a zip of the
 * Windows binary with a README, a zipped Orgus.app for macOS, a tar.gz with install.sh for Linux.
 * A node never downloads them (it updates from `targets`); they are signed so a person, or a
 * download page, can check what they got. Built by `node agents/node-agent/scripts/build.mjs package`.
 */
export const RELEASE_PACKAGES = ['darwin-amd64-app', 'darwin-arm64-app', 'linux-amd64-tar', 'linux-arm64-tar', 'windows-amd64-zip', 'windows-arm64-zip'] as const;
export type ReleasePackage = (typeof RELEASE_PACKAGES)[number];

/** How many release keys a node trusts at once: two, one of them kept in a safe (ADR 0042 owner question 6). */
export const RELEASE_KEY_SLOTS = 2;
/** At most this many revoked versions in one manifest. */
export const RELEASE_MAX_REVOKED = 256;

/** One published file. */
export interface ReleaseFile {
  /** The name it is published under, next to the manifest: `[A-Za-z0-9][A-Za-z0-9._-]{0,127}`. */
  file: string;
  /** Bytes; a safe integer of at least 1. */
  size: number;
  /** Lowercase hex. */
  sha256: string;
}

export interface ReleaseManifest {
  format: typeof RELEASE_MANIFEST_FORMAT;
  /** `MAJOR.MINOR.PATCH` with an optional `-suffix` (a build flavour such as `-go`). */
  version: string;
  /** The commit the binaries were built from, 40 lowercase hex. */
  commit: string;
  /** One to six of RELEASE_TARGETS. */
  targets: Partial<Record<ReleaseTarget, ReleaseFile>>;
  /**
   * Optional: one to six of RELEASE_PACKAGES. Absent in a manifest of binaries only (what
   * `build.mjs release` writes); when present it is signed like everything else, and no file name
   * is shared with a target.
   */
  packages?: Partial<Record<ReleasePackage, ReleaseFile>>;
  /** Nodes running a lower version update first and run read-only until then. At most `version`. */
  floor: string;
  /** Versions no node may install or keep running. Never contains `version`. */
  revoked: string[];
  /** `YYYY-MM-DDTHH:MM:SSZ`; set by the signer, empty in a draft. */
  issuedAt: string;
  /** `YYYY-MM-DDTHH:MM:SSZ`, after issuedAt; set by the signer, empty in a draft. */
  expires: string;
  /** The release key that signed it (see ReleaseKey.keyId); set by the signer, empty in a draft. */
  keyId: string;
}

/** The published document. */
export interface SignedReleaseManifest {
  manifest: ReleaseManifest;
  /** Ed25519 over releaseSigningInput(manifest), standard base64 (88 characters). */
  signature: string;
}

/** A trusted release public key, as built into the node. */
export interface ReleaseKey {
  /** The first 8 bytes of SHA-256 over the raw 32-byte public key, lowercase hex (16 characters). */
  keyId: string;
  /** The raw 32-byte Ed25519 public key, standard base64. */
  publicKey: string;
}

export type ReleaseRefusal = 'malformed' | 'unknown-key' | 'bad-signature' | 'expired';

export const RELEASE_REFUSAL_LABEL: Record<ReleaseRefusal, string> = {
  malformed: 'The release manifest is not a valid v1 manifest',
  'unknown-key': 'The release manifest is signed by a key this node does not trust',
  'bad-signature': 'The release manifest signature does not match its contents',
  expired: 'The release manifest has expired',
};

export type ReleaseCheck =
  | { ok: true; manifest: ReleaseManifest; key: ReleaseKey }
  | { ok: false; reason: ReleaseRefusal; detail: string };

/** Checks an Ed25519 signature: raw public key, message bytes as UTF-8 text, signature, all as given. */
export type Ed25519Verify = (publicKeyBase64: string, message: string, signatureBase64: string) => boolean | Promise<boolean>;

// ---------------------------------------------------------------------------
// The grammar (mirrored line for line in agents/node-agent/internal/release/manifest.go)
// ---------------------------------------------------------------------------

const VERSION_RE = /^(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})(-[0-9A-Za-z][0-9A-Za-z.-]{0,31})?$/;
const COMMIT_RE = /^[0-9a-f]{40}$/;
const FILE_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const SHA256_RE = /^[0-9a-f]{64}$/;
const TIME_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}Z$/;
const KEY_ID_RE = /^[0-9a-f]{16}$/;
const SIGNATURE_RE = /^[A-Za-z0-9+/]{86}==$/;

const MANIFEST_FIELDS = ['commit', 'expires', 'floor', 'format', 'issuedAt', 'keyId', 'revoked', 'targets', 'version'] as const;
/** Fields a manifest may leave out. */
const OPTIONAL_MANIFEST_FIELDS = ['packages'] as const;
const FILE_FIELDS = ['file', 'sha256', 'size'] as const;
const ENVELOPE_FIELDS = ['manifest', 'signature'] as const;

export function isReleaseVersion(v: unknown): v is string {
  return typeof v === 'string' && VERSION_RE.test(v);
}

/**
 * Orders two release versions: the numeric triple first, then the suffix byte by byte, no
 * suffix first. A suffix is a build flavour, not a pre-release: releases ship distinct triples.
 * Throws on a string that is not a release version.
 */
export function compareReleaseVersions(a: string, b: string): number {
  const pa = VERSION_RE.exec(a);
  const pb = VERSION_RE.exec(b);
  if (!pa || !pb) throw new Error(`not a release version: ${pa ? b : a}`);
  for (let i = 1; i <= 3; i++) {
    const d = Number(pa[i]) - Number(pb[i]);
    if (d) return d < 0 ? -1 : 1;
  }
  const sa = pa[4] ?? '';
  const sb = pb[4] ?? '';
  return sa === sb ? 0 : sa < sb ? -1 : 1;
}

/** Milliseconds since the epoch for a `YYYY-MM-DDTHH:MM:SSZ` time, or null when it is not a real one. */
export function parseReleaseTime(s: unknown): number | null {
  if (typeof s !== 'string' || !TIME_RE.test(s)) return null;
  const ms = Date.parse(s);
  if (!Number.isFinite(ms)) return null;
  // Round trip, so 2026-02-30 or 23:59:60 is refused rather than rolled over.
  return new Date(ms).toISOString().replace('.000Z', 'Z') === s ? ms : null;
}

/** `YYYY-MM-DDTHH:MM:SSZ` for a time in milliseconds (whole seconds, rounded down). */
export function formatReleaseTime(ms: number): string {
  return new Date(Math.floor(ms / 1000) * 1000).toISOString().replace('.000Z', 'Z');
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function exactFields(o: Record<string, unknown>, fields: readonly string[], where: string, optional: readonly string[] = []): string | null {
  for (const k of Object.keys(o)) if (!fields.includes(k) && !optional.includes(k)) return `${where} has an unknown field "${k}"`;
  for (const k of fields) if (!Object.hasOwn(o, k)) return `${where} has no "${k}"`;
  return null;
}

/** Checks one published file (a target or a package); `files` holds the names seen so far. */
function releaseFileProblem(t: unknown, where: string, files: Set<string>): string | null {
  if (!isObject(t)) return `${where} is not an object`;
  const tf = exactFields(t, FILE_FIELDS, where);
  if (tf) return tf;
  if (typeof t.file !== 'string' || !FILE_RE.test(t.file)) return `${where}: file is not a plain file name`;
  if (files.has(t.file)) return `${where}: file ${t.file} is used twice`;
  files.add(t.file);
  if (typeof t.size !== 'number' || !Number.isSafeInteger(t.size) || t.size < 1) return `${where}: size is not a whole number of bytes`;
  if (typeof t.sha256 !== 'string' || !SHA256_RE.test(t.sha256)) return `${where}: sha256 is not 64 lowercase hex characters`;
  return null;
}

/**
 * Checks a parsed manifest against the v1 grammar. A draft (what `build.mjs release` writes,
 * before signing) has issuedAt, expires and keyId all empty; a signed manifest has all three.
 * Returns the problem, or null when it is valid.
 */
export function releaseManifestProblem(m: unknown, opts: { draft?: boolean } = {}): string | null {
  if (!isObject(m)) return 'the manifest is not an object';
  const fields = exactFields(m, MANIFEST_FIELDS, 'the manifest', OPTIONAL_MANIFEST_FIELDS);
  if (fields) return fields;
  if (m.format !== RELEASE_MANIFEST_FORMAT) return `format is not "${RELEASE_MANIFEST_FORMAT}"`;
  if (!isReleaseVersion(m.version)) return 'version is not MAJOR.MINOR.PATCH with an optional -suffix';
  if (typeof m.commit !== 'string' || !COMMIT_RE.test(m.commit)) return 'commit is not 40 lowercase hex characters';
  if (!isObject(m.targets)) return 'targets is not an object';
  const names = Object.keys(m.targets);
  if (names.length === 0) return 'targets is empty';
  const files = new Set<string>();
  for (const name of names) {
    if (!(RELEASE_TARGETS as readonly string[]).includes(name)) return `target "${name}" is not one of ${RELEASE_TARGETS.join(', ')}`;
    const problem = releaseFileProblem(m.targets[name], `target ${name}`, files);
    if (problem) return problem;
  }
  if (Object.hasOwn(m, 'packages')) {
    if (!isObject(m.packages)) return 'packages is not an object';
    const kinds = Object.keys(m.packages);
    if (kinds.length === 0) return 'packages is empty (leave it out instead)';
    for (const name of kinds) {
      if (!(RELEASE_PACKAGES as readonly string[]).includes(name)) return `package "${name}" is not one of ${RELEASE_PACKAGES.join(', ')}`;
      const problem = releaseFileProblem(m.packages[name], `package ${name}`, files);
      if (problem) return problem;
    }
  }
  if (!isReleaseVersion(m.floor)) return 'floor is not a release version';
  if (compareReleaseVersions(m.floor, m.version) > 0) return 'floor is above version';
  if (!Array.isArray(m.revoked)) return 'revoked is not a list';
  if (m.revoked.length > RELEASE_MAX_REVOKED) return `revoked lists more than ${RELEASE_MAX_REVOKED} versions`;
  const seen = new Set<string>();
  for (const r of m.revoked) {
    if (!isReleaseVersion(r)) return 'revoked holds something that is not a release version';
    if (seen.has(r)) return `revoked lists ${r} twice`;
    if (r === m.version) return 'the manifest revokes its own version';
    seen.add(r);
  }
  if (opts.draft) {
    if (m.issuedAt !== '' || m.expires !== '' || m.keyId !== '') return 'a draft leaves issuedAt, expires and keyId empty; the signer sets them';
    return null;
  }
  const issued = parseReleaseTime(m.issuedAt);
  if (issued === null) return 'issuedAt is not YYYY-MM-DDTHH:MM:SSZ';
  const expires = parseReleaseTime(m.expires);
  if (expires === null) return 'expires is not YYYY-MM-DDTHH:MM:SSZ';
  if (expires <= issued) return 'expires is not after issuedAt';
  if (typeof m.keyId !== 'string' || !KEY_ID_RE.test(m.keyId)) return 'keyId is not 16 lowercase hex characters';
  return null;
}

/** The manifest as canonical JSON: keys sorted at every level, no whitespace. Only for a valid manifest. */
export function canonicalReleaseManifest(m: ReleaseManifest): string {
  const str = (s: string) => `"${s}"`; // the grammar admits no character JSON would escape
  const files = (o: Record<string, ReleaseFile>) =>
    Object.keys(o)
      .sort()
      .map((name) => {
        const t = o[name]!;
        return `${str(name)}:{"file":${str(t.file)},"sha256":${str(t.sha256)},"size":${String(t.size)}}`;
      })
      .join(',');
  // packages sorts between keyId and revoked, and is left out when absent.
  const packages = m.packages === undefined ? '' : `"packages":{${files(m.packages as Record<string, ReleaseFile>)}},`;
  return (
    `{"commit":${str(m.commit)},"expires":${str(m.expires)},"floor":${str(m.floor)},"format":${str(m.format)},` +
    `"issuedAt":${str(m.issuedAt)},"keyId":${str(m.keyId)},${packages}"revoked":[${m.revoked.map(str).join(',')}],` +
    `"targets":{${files(m.targets as Record<string, ReleaseFile>)}},"version":${str(m.version)}}`
  );
}

/** The exact text that gets signed (as UTF-8 bytes; it is all ASCII). */
export function releaseSigningInput(m: ReleaseManifest): string {
  return `${RELEASE_MANIFEST_FORMAT}\n${canonicalReleaseManifest(m)}`;
}

/** The published document for a manifest and its signature, as written to disk. */
export function serializeSignedRelease(doc: SignedReleaseManifest): string {
  return `{"manifest":${canonicalReleaseManifest(doc.manifest)},"signature":"${doc.signature}"}\n`;
}

/** Parses a published document without checking the signature: the grammar only. */
export function parseSignedRelease(text: string): { ok: true; doc: SignedReleaseManifest } | { ok: false; reason: 'malformed'; detail: string } {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'malformed', detail: 'the document is not JSON' };
  }
  if (!isObject(v)) return { ok: false, reason: 'malformed', detail: 'the document is not an object' };
  const env = exactFields(v, ENVELOPE_FIELDS, 'the document');
  if (env) return { ok: false, reason: 'malformed', detail: env };
  if (typeof v.signature !== 'string' || !SIGNATURE_RE.test(v.signature)) return { ok: false, reason: 'malformed', detail: 'signature is not 64 bytes of base64' };
  const problem = releaseManifestProblem(v.manifest);
  if (problem) return { ok: false, reason: 'malformed', detail: problem };
  return { ok: true, doc: { manifest: v.manifest as ReleaseManifest, signature: v.signature } };
}

/**
 * Verifies a published document against the trusted keys at `now` (milliseconds). The node
 * keeps at most RELEASE_KEY_SLOTS keys; `verify` does the Ed25519 check.
 */
export async function verifyReleaseManifest(text: string, opts: { keys: readonly ReleaseKey[]; now: number; verify: Ed25519Verify }): Promise<ReleaseCheck> {
  const parsed = parseSignedRelease(text);
  if (!parsed.ok) return parsed;
  const { manifest, signature } = parsed.doc;
  const key = opts.keys.find((k) => k.keyId === manifest.keyId);
  if (!key) return { ok: false, reason: 'unknown-key', detail: `key ${manifest.keyId} is not one of the ${opts.keys.length} trusted release keys` };
  let good = false;
  try {
    good = await opts.verify(key.publicKey, releaseSigningInput(manifest), signature);
  } catch {
    good = false;
  }
  if (!good) return { ok: false, reason: 'bad-signature', detail: `the signature does not verify under key ${key.keyId}` };
  if (opts.now >= parseReleaseTime(manifest.expires)!) return { ok: false, reason: 'expired', detail: `the manifest expired at ${manifest.expires}` };
  return { ok: true, manifest, key };
}

// ---------------------------------------------------------------------------
// What a verified manifest means for a node
// ---------------------------------------------------------------------------

/**
 * Where a node running `running` stands against a verified manifest:
 * 'revoked' (stop and update), 'below-floor' (update first, read-only until then),
 * 'update-available', 'current', or 'ahead' (newer than the manifest: a lagging server, alert).
 */
export type ReleaseStanding = 'revoked' | 'below-floor' | 'update-available' | 'current' | 'ahead';

export function releaseStanding(m: ReleaseManifest, running: string): ReleaseStanding {
  if (m.revoked.includes(running)) return 'revoked';
  if (compareReleaseVersions(running, m.floor) < 0) return 'below-floor';
  const d = compareReleaseVersions(running, m.version);
  return d < 0 ? 'update-available' : d === 0 ? 'current' : 'ahead';
}

/**
 * Whether a node may install the manifest's version, given the highest version it has ever
 * installed (its stored floor): never below it. A rollback is `orgus rollback` with the user present.
 */
export function releaseInstallCheck(m: ReleaseManifest, highestInstalled: string): { ok: true } | { ok: false; reason: 'rollback'; detail: string } {
  if (compareReleaseVersions(m.version, highestInstalled) < 0) {
    return { ok: false, reason: 'rollback', detail: `${m.version} is below ${highestInstalled}, the highest version installed here` };
  }
  return { ok: true };
}
